import React, { useState, useEffect } from 'react';
import { useParams, Routes, Route, Link, Navigate } from 'react-router-dom';
import { useProjectLoader, ProjectProvider } from '../contexts/ProjectContext';
import PhaseNavigator from '../components/PhaseNavigator';
import FileExplorer from '../components/FileExplorer';
import PlanningPhase from '../components/phases/PlanningPhase';
import DesignPhase from '../components/phases/DesignPhase';
import CodingPhase from '../components/phases/CodingPhase';
import RefinementPhase from '../components/phases/RefinementPhase';
import FinishingPhase from '../components/phases/FinishingPhase';
import CodeEditor from '../components/CodeEditor';
import FileDiff from '../components/FileDiff';
import ProjectActionsBar from '../components/ProjectActionsBar';
import LoadingSpinner from '../components/LoadingSpinner';
import { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { exportProject } from '../utils/projectUtils';

/**
 * Project editor component
 * Main workspace for editing and implementing a project
 */
function ProjectEditor() {
  const { projectId } = useParams();
  const { project, updateProject, loading, error } = useProjectLoader(projectId);
  const [currentPhase, setCurrentPhase] = useState('PLANNING');
  const { isPremium } = useContext(AuthContext);

  // Update current phase when project changes
  useEffect(() => {
    if (project) {
      setCurrentPhase(project.currentPhase || 'PLANNING');
    }
  }, [project]);

  // Handle phase change
  const handlePhaseChange = async (newPhase) => {
    if (!project) return;
    
    // Update project phase
    const updatedProject = {
      ...project,
      currentPhase: newPhase,
      updated: Date.now()
    };
    
    const success = await updateProject(updatedProject);
    if (success) {
      setCurrentPhase(newPhase);
    }
  };

  // Handle project export
  const handleExportProject = async () => {
    try {
      if (!projectId) return;
      
      // Export project data
      const exportBlob = await exportProject(projectId);
      
      // Create download link
      const url = URL.createObjectURL(exportBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name.replace(/\s+/g, '-')}_export.json`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error exporting project:', error);
      alert('Failed to export project');
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <LoadingSpinner message="Loading project..." />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-danger)' }}>
        <h2>Error Loading Project</h2>
        <p>{error}</p>
        <Link to="/" style={{ display: 'inline-block', marginTop: '1rem' }}>
          Return to Home
        </Link>
      </div>
    );
  }

  // Show 404 state
  if (!project) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Project Not Found</h2>
        <p>The project you're looking for doesn't exist or has been deleted.</p>
        <Link to="/" style={{ display: 'inline-block', marginTop: '1rem' }}>
          Return to Home
        </Link>
      </div>
    );
  }

  // All available phases
  const phases = ['PLANNING', 'DESIGN', 'CODING', 'REFINEMENT', 'FINISHING'];

  return (
    <ProjectProvider project={project} updateProject={updateProject}>
      <div style={{ padding: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h1>{project.name}</h1>
          <ProjectActionsBar 
            onExport={handleExportProject}
            showPremiumBadge={isPremium}
          />
        </div>
        
        <PhaseNavigator 
          phases={phases}
          currentPhase={currentPhase}
          onPhaseChange={handlePhaseChange}
        />
        
        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
          {/* Sidebar */}
          <div style={{ width: '250px', flexShrink: 0 }}>
            <FileExplorer treeView={true} />
          </div>
          
          {/* Main content */}
          <div style={{ flex: 1 }}>
            <Routes>
              <Route path="/" element={
                currentPhase === 'PLANNING' ? <PlanningPhase /> :
                currentPhase === 'DESIGN' ? <DesignPhase /> :
                currentPhase === 'CODING' ? <CodingPhase /> :
                currentPhase === 'REFINEMENT' ? <RefinementPhase /> :
                <FinishingPhase />
              } />
              <Route path="/components" element={<FileExplorer treeView={false} />} />
              <Route path="/files" element={<FileExplorer treeView={true} />} />
              <Route path="/file/:fileId" element={<CodeEditor />} />
              <Route path="/file/:fileId/history" element={<FileDiff />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </ProjectProvider>
  );
}

export default ProjectEditor;